/**
 * Reusable style objects for AI components
 * Common sx prop configurations that can be imported and used across components
 */

import { SxProps, Theme } from '@mui/material';
import { AI_COLORS, AI_GRADIENTS, AI_SHADOWS, AI_BORDERS, AI_BORDER_RADIUS, AI_TRANSITIONS, AI_FILTERS } from './aiTheme';
import { pulseAnimation, recordingPulseAnimation, shimmerAnimation, glowAnimation, gradientShiftAnimation } from './aiAnimations';

// Backdrop styles
export const aiBackdropStyles: SxProps<Theme> = {
  zIndex: 1301,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  backdropFilter: AI_FILTERS.blur.light,
};

// AI Loader card
export const aiLoaderCardStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  padding: 4,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: AI_BORDER_RADIUS.large,
  boxShadow: AI_SHADOWS.card,
  backdropFilter: AI_FILTERS.blur.medium,
  border: AI_BORDERS.white,
  minWidth: '350px',
  textAlign: 'center',
};

// AI Icon with pulse animation
export const aiIconStyles: SxProps<Theme> = {
  width: 80,
  height: 80,
  borderRadius: AI_BORDER_RADIUS.circle,
  background: AI_GRADIENTS.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: `${pulseAnimation} 2s infinite ease-in-out`,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: AI_BORDER_RADIUS.circle,
    background: AI_GRADIENTS.primary,
    opacity: 0.3,
    animation: `${pulseAnimation} 2s infinite ease-in-out 0.5s`,
    zIndex: -1,
  },
};

// Recording indicator dot
export const recordingDotStyles: SxProps<Theme> = {
  backgroundColor: AI_COLORS.status.recording,
  height: 12,
  width: 12,
  borderRadius: AI_BORDER_RADIUS.circle,
  animation: `${recordingPulseAnimation} 1.5s infinite`,
};

// AI Toggle container
export const aiToggleContainerStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  mt: 1,
  p: 2,
  borderRadius: AI_BORDER_RADIUS.medium,
  background: AI_GRADIENTS.backgroundLight,
  border: AI_BORDERS.primary,
  transition: AI_TRANSITIONS.fast,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: AI_GRADIENTS.shimmer,
    animation: `${shimmerAnimation} 3s infinite`,
  },
  '&:hover': {
    background: AI_GRADIENTS.backgroundLightHover,
    border: AI_BORDERS.primaryHover,
    '&::before': {
      animationDuration: '1.5s',
    },
  },
};

// AI emoji with glow
export const aiEmojiGlowStyles: SxProps<Theme> = {
  fontSize: '24px',
  filter: AI_FILTERS.dropShadow.light,
  animation: `${glowAnimation} 2s infinite alternate`,
};

// AI gradient text
export const aiGradientTextStyles: SxProps<Theme> = {
  fontWeight: 600,
  fontSize: '16px',
  background: AI_GRADIENTS.text,
  backgroundSize: '200% 100%',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  letterSpacing: '0.5px',
  animation: `${gradientShiftAnimation} 4s ease-in-out infinite`,
};

// Audio component container
export const audioComponentStyles: SxProps<Theme> = {
  p: 2,
  borderRadius: 2,
  background: AI_GRADIENTS.background,
  border: AI_BORDERS.light,
  transition: AI_TRANSITIONS.default,
  '&:hover': {
    borderColor: AI_COLORS.primary,
    boxShadow: AI_SHADOWS.light,
  },
  '& audio': {
    width: '100%',
    outline: 'none',
    '&::-webkit-media-controls-panel': {
      background: AI_GRADIENTS.audioControls,
    },
  },
};

// Audio loading container
export const audioLoadingStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  py: 2,
  px: 3,
  borderRadius: 2,
  background: AI_GRADIENTS.background,
  border: AI_BORDERS.light,
};

// Audio record container
export const audioRecordContainerStyles: SxProps<Theme> = {
  mt: 2,
  border: AI_BORDERS.light,
  p: 3,
  borderRadius: 2,
  background: AI_GRADIENTS.background,
  transition: AI_TRANSITIONS.default,
  ':hover': {
    borderColor: AI_COLORS.primary,
    boxShadow: AI_SHADOWS.hover,
  },
};

// Buttons
export const primaryButtonStyles: SxProps<Theme> = {
  width: '100%',
  background: AI_GRADIENTS.primary,
  color: 'white',
  fontWeight: 600,
  py: 1.5,
  borderRadius: 2,
  textTransform: 'none',
  boxShadow: AI_SHADOWS.button,
  transition: AI_TRANSITIONS.default,
  '&:hover': {
    background: AI_GRADIENTS.primaryReverse,
    boxShadow: AI_SHADOWS.buttonHover,
    transform: 'translateY(-2px)',
  },
};

export const recordingStopButtonStyles: SxProps<Theme> = {
  background: AI_GRADIENTS.recording,
  color: 'white',
  fontWeight: 600,
  px: 3,
  py: 1,
  borderRadius: 2,
  textTransform: 'none',
  boxShadow: AI_SHADOWS.recording,
  transition: AI_TRANSITIONS.default,
  '&:hover': {
    background: AI_GRADIENTS.recordingHover,
    boxShadow: AI_SHADOWS.recordingHover,
    transform: 'translateY(-2px)',
  },
};

export const clearButtonStyles: SxProps<Theme> = {
  flex: 1,
  background: AI_GRADIENTS.neutral,
  color: 'white',
  fontWeight: 600,
  py: 1.2,
  borderRadius: 2,
  textTransform: 'none',
  boxShadow: AI_SHADOWS.neutral,
  transition: AI_TRANSITIONS.default,
  '&:hover': {
    background: AI_GRADIENTS.neutralHover,
    boxShadow: AI_SHADOWS.neutralHover,
    transform: 'translateY(-2px)',
  },
};

export const submitButtonStyles: SxProps<Theme> = {
  flex: 1,
  background: AI_GRADIENTS.success,
  color: 'white',
  fontWeight: 600,
  py: 1.2,
  borderRadius: 2,
  textTransform: 'none',
  boxShadow: AI_SHADOWS.success,
  transition: AI_TRANSITIONS.default,
  '&:hover': {
    background: AI_GRADIENTS.successHover,
    boxShadow: AI_SHADOWS.successHover,
    transform: 'translateY(-2px)',
  },
};

// Switch styles
export const aiSwitchStyles: SxProps<Theme> = {
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: AI_COLORS.primary,
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: AI_COLORS.primary,
  },
};

// CircularProgress styles
export const aiCircularProgressStyles: SxProps<Theme> = {
  color: AI_COLORS.primary,
  '& .MuiCircularProgress-circle': {
    strokeLinecap: 'round',
  },
};
